import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { C, SectionLabel, H2, BtnOrange, wrap, metalGradient } from './styles';
import Nav from './components/Nav';
import Footer from './components/Footer';

const steps = [
  {
    n: '01',
    t: 'Upload Your Sketch or Photo',
    b: 'Snap a photo of your hand sketch, a rough drawing, or even a napkin sketch. No CAD files or technical drawings required — just enough detail to communicate what you need.',
  },
  {
    n: '02',
    t: 'Review AI Concept Renderings',
    b: 'Our team transforms your sketch into concept visualizations within 48–72 hours, using AI rendering tools to speed up the visuals, so you can confirm scope and design intent before anything is sent to a shop floor.',
    note: 'These renderings are AI-generated concept visualizations showing approximate appearance. They are not engineering drawings. Final dimensions, specifications, and manufacturing details will be confirmed by the manufacturer during the quoting process.',
  },
  {
    n: '03',
    t: 'Get Matched with Ontario Fabricators',
    b: 'Your approved brief is routed to 2–3 pre-vetted Ontario fabricators with demonstrated experience in the relevant material and process.',
  },
  {
    n: '04',
    t: 'Compare Quotes and Choose Your Manufacturer',
    b: 'Review structured, side-by-side quotes from matched fabricators and choose the manufacturer that best fits your budget, timeline, and quality requirements.',
  },
];

const audiences = [
  { t: 'HVAC Contractors', b: 'Custom ductwork, brackets, and fixtures fabricated to spec without an in-house metal shop.' },
  { t: 'Restaurant Owners', b: 'Stainless steel fixtures, shelving, and equipment components built to commercial standards.' },
  { t: 'Inventors', b: 'Turn a first sketch into a working prototype without needing CAD experience or fabrication contacts.' },
  { t: 'Construction Companies', b: 'Custom metal components sourced for active projects without managing multiple supplier relationships.' },
];

const faqs = [
  { q: 'Do I need CAD files to submit a project?', a: 'No. A hand sketch, a photograph of a rough drawing, or a clear written description is enough to get started. We handle the translation into a structured fabrication brief.' },
  { q: 'How much does it cost to submit a project?', a: 'Nothing. Submitting a project is free — brief preparation and fabricator matching cost you nothing. We are paid a success fee by the manufacturer when a deal closes, never by you. There is no obligation to proceed with any quote you receive.' },
  { q: 'How accurate are the AI concept renderings?', a: 'Renderings are concept visualizations intended to confirm scope and design intent — they are not engineering-grade drawings. Exact tolerances and final specifications are confirmed directly with the manufacturer during quoting.' },
  { q: 'How long does the whole process take?', a: 'Most clients receive concept renderings within 48–72 business hours of submission. Manufacturer quotes typically follow within 5–10 business days, depending on project complexity and shop availability.' },
  { q: 'Where are the manufacturers located?', a: 'MetalMatch works exclusively with established Ontario-based manufacturers and fabrication shops, all reviewed for capability, capacity, and track record before joining the network.' },
  { q: 'What types of fabrication can MetalMatch handle?', a: 'CNC machining, welding and welded assemblies, stainless steel fixtures, sheet metal fabrication, prototypes, and full production runs — from one-off parts to recurring orders.' },
];

function StepCard({ s }) {
  return (
    <div style={{
      background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10,
      display: 'grid', gridTemplateColumns: '90px 1fr', gap: 20,
      alignItems: 'center', padding: '26px 30px', transition: 'border-color 0.2s',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = C.navy}
      onMouseLeave={e => e.currentTarget.style.borderColor = C.bdr}
    >
      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 78, fontWeight: 700, color: C.bdr, lineHeight: 1, userSelect: 'none' }}>{s.n}</div>
      <div>
        <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 23, fontWeight: 700, textTransform: 'uppercase', color: C.txt, marginBottom: 8 }}>{s.t}</h3>
        <p style={{ color: C.mut, fontSize: 14, lineHeight: 1.7 }}>{s.b}</p>
        {s.note && (
          <div style={{ background: C.navybg, border: `1px solid rgba(50,151,219,0.3)`, borderRadius: 6, padding: '10px 14px', marginTop: 14 }}>
            <p style={{ fontSize: 12, color: C.navy, lineHeight: 1.6 }}>{s.note}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HowItWorksPage() {
  const [open, setOpen] = useState(null);
  const toggle = (i) => setOpen(open === i ? null : i);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'How It Works — MetalMatch';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <>
      <Nav />

      {/* Hero */}
      <section style={{ padding: '80px 0 90px', borderBottom: `1px solid ${C.bdr}` }}>
        <div style={{ ...wrap, textAlign: 'center' }}>
          <SectionLabel>How it works</SectionLabel>
          <H2 style={{ maxWidth: 780, margin: '0 auto 20px' }}>
            <span style={metalGradient}>From hand sketch to competitive quotes</span>
            <br />
            <span style={{ color: C.orange }}>— in 4 simple steps</span>
          </H2>
          <p style={{ color: C.mut, fontSize: 16, lineHeight: 1.75, maxWidth: 560, margin: '0 auto 30px' }}>
            No CAD required, no engineering costs upfront. Here is exactly what happens between submitting a sketch and choosing a manufacturer.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <BtnOrange>Submit Your Project →</BtnOrange>
            </Link>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section style={{ padding: '88px 0', borderBottom: `1px solid ${C.bdr}` }}>
        <div style={wrap}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {steps.map((s) => <StepCard key={s.n} s={s} />)}
          </div>
        </div>
      </section>

      {/* Who Uses MetalMatch */}
      <section style={{ padding: '88px 0', borderBottom: `1px solid ${C.bdr}`, background: C.surf }}>
        <div style={wrap}>
          <SectionLabel>Who it's for</SectionLabel>
          <H2 style={{ marginBottom: 38 }}>Who uses MetalMatch</H2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {audiences.map(({ t, b }) => (
              <div key={t} style={{ background: C.surf2, border: `1px solid ${C.bdr}`, borderRadius: 8, padding: '20px 22px' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.navy, flexShrink: 0, marginTop: 7 }} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.txt, marginBottom: 4 }}>{t}</div>
                    <p style={{ fontSize: 12.5, color: C.mut, lineHeight: 1.65 }}>{b}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '88px 0', borderBottom: `1px solid ${C.bdr}` }}>
        <div style={wrap}>
          <SectionLabel>Common questions</SectionLabel>
          <H2 style={{ marginBottom: 38 }}>Everything you need to know</H2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {faqs.map((f, i) => (
              <div key={i} style={{ border: `1px solid ${open === i ? C.bdr2 : C.bdr}`, borderRadius: 7, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                <button onClick={() => toggle(i)} style={{
                  width: '100%', background: open === i ? C.surf3 : C.surf,
                  border: 'none', padding: '18px 22px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s',
                }}>
                  <span style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: 14, fontWeight: 600, color: C.txt }}>{f.q}</span>
                  <span style={{ color: C.orange, fontSize: 20, flexShrink: 0, marginLeft: 14 }}>{open === i ? '−' : '+'}</span>
                </button>
                {open === i && (
                  <div style={{ padding: '0 22px 18px', background: C.surf3 }}>
                    <p style={{ fontSize: 13, color: C.mut, lineHeight: 1.75, marginTop: 4 }}>{f.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 0' }}>
        <div style={wrap}>
          <div style={{
            borderTop: `1px solid rgba(232,76,61,0.35)`,
            borderBottom: `1px solid rgba(232,76,61,0.35)`,
            padding: '64px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at center, rgba(50,151,219,0.08) 0%, transparent 70%)`, pointerEvents: 'none' }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.orange, display: 'block', marginBottom: 12 }}>Ready to begin?</span>
            <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(28px, 5vw, 50px)', fontWeight: 700, textTransform: 'uppercase', color: C.txt, marginBottom: 14 }}>Get your project matched.</h2>
            <p style={{ color: C.mut, fontSize: 16, maxWidth: 460, margin: '0 auto 30px', lineHeight: 1.7 }}>
              No technical drawings required. No upfront engineering costs. Just a sketch and a clear description of what you need made.
            </p>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <BtnOrange>Submit Your Project →</BtnOrange>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
